import { Injectable, inject } from '@angular/core';
import { ROOT_SCOPE_PORT } from '../utils';
import { HelpersService } from '../helpers/helpers.service';
import { IRootScopeService } from 'angular';

interface DateFormatsPrefs {
  startDayOfWeek?: string;
  dateFormat?: string;
  hourFormat?: string;
}

interface PreferencesData {
  hourFormat: string;
  dateFormat: string;
  startDayOfWeek: string;
  accessibilityEnabled: boolean;
}

/**
 * UserPreferencesService (Angular 18)
 *
 * Replaces: apps/b2b-ui/common/services/userPreferences/userPreferences.service.js
 * Manages user date/time format preferences and accessibility settings.
 */
@Injectable({ providedIn: 'root' })
export class UserPreferencesService {
  private helpers = inject(HelpersService);
  private rootScope = inject<IRootScopeService>(ROOT_SCOPE_PORT);

  // ─── State ────────────────────────────────────────────────────────────────
  dateFormats: DateFormatsPrefs = {};
  adminSettings: Record<string, unknown> = {};
  userSettings: Record<string, unknown> = {};
  preferences: PreferencesData | false = false;
  loaded = false;

  // ─── Static settings ─────────────────────────────────────────────────────
  readonly settings = {
    dateFormat: [
      'EEEE, MMMM d, yyyy',
      'MMM. d, yyyy',
      'MM-dd-yyyy',
      'MM/dd/yyyy',
      'dd-MM-yy',
      'MM/dd/yy',
      'dd-MM-yyyy',
      'dd/MM/yyyy',
      'yyyy-MM-dd',
      'yyyy MMMM d',
      'yyyy-MM-dd',
      'yyyy-MMM-dd',
      'M/d/yy',
    ],
    hourFormat: ['h:mm a', 'HH:mm'],
    startDayOfWeek: ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'],
  };

  readonly weekMap: Record<string, number> = {
    SUNDAY: 0,
    MONDAY: 1,
    TUESDAY: 2,
    WEDNESDAY: 3,
    THURSDAY: 4,
    FRIDAY: 5,
    SATURDAY: 6,
  };

  // ─── Initialization ───────────────────────────────────────────────────────

  init(): boolean {
    return true;
  }

  // ─── Date Format retrieval ────────────────────────────────────────────────

  getDateFormat(type: string, format?: string): string {
    if (format && this.hasPreferences(format)) {
      return (this.dateFormats as Record<string, string>)[format];
    }
    return (this.helpers.dateFormats as Record<string, string>)[type];
  }

  getDateTimeFormat(type: string): string {
    if (this.hasPreferences('dateFormat') && this.hasPreferences('hourFormat')) {
      return `${this.dateFormats.dateFormat} 'at' ${this.dateFormats.hourFormat}`;
    }
    return (this.helpers.dateFormats as Record<string, string>)[type];
  }

  hasPreferences(format?: string): boolean {
    if (!format) {
      return !!this.preferences;
    }
    return !!(this.dateFormats as Record<string, unknown>)[format];
  }

  // ─── Preference processing ────────────────────────────────────────────────

  process(admin?: Record<string, unknown>, user?: Record<string, unknown>): void {
    this.adminSettings = admin || this.adminSettings;
    this.userSettings = user || this.userSettings;

    const result: Record<string, unknown> = {
      hourFormat: '',
      dateFormat: '',
      startDayOfWeek: '',
      accessibilityEnabled: true,
    };

    for (const key of Object.keys(result)) {
      result[key] = this.userSettings[key] ? this.userSettings[key] : this.adminSettings[key];
    }

    this.setPreferences(result as unknown as PreferencesData);
    this.setFormat(result as DateFormatsPrefs);
  }

  setPreferences(prefs: PreferencesData | false): void {
    this.preferences = prefs;
  }

  getPreferences(): PreferencesData | false {
    return this.preferences;
  }

  // ─── Format setting ───────────────────────────────────────────────────────

  setFormat(user: DateFormatsPrefs): void {
    this.dateFormats.startDayOfWeek = user.startDayOfWeek || this.settings.startDayOfWeek[6];
    this.dateFormats.dateFormat = user.dateFormat || this.getDateFormat('defaultUserPreferenceFormat');
    this.dateFormats.hourFormat = user.hourFormat || this.getHourFormat();

    // Broadcast event for legacy consumers
    this.rootScope.$broadcast('UserPreferencesService:onDateFormatLoad');
    this.loaded = true;
  }

  // ─── Convenience getters ──────────────────────────────────────────────────

  getStartDayOfWeek(): number {
    if (this.dateFormats.startDayOfWeek) {
      return this.weekMap[this.dateFormats.startDayOfWeek];
    }
    return this.weekMap['SUNDAY'];
  }

  getStartDayOfWeekString(): string {
    return this.dateFormats.startDayOfWeek || this.settings.startDayOfWeek[6];
  }

  getHourFormat(): string {
    if (this.dateFormats.hourFormat) {
      return this.dateFormats.hourFormat;
    }
    return this.settings.hourFormat[0];
  }
}
